import type { RouteDef } from '../router';
import { trialCtaHtml } from '../trial-cta';
import type { TranslationKey } from '../i18n/types';
import { articlesPending, getCountryArticle, listCountryArticleSlugs, blogLocalesFor } from '../data/articles';
import { getCountryBySlug, allCountries } from '../data/prices';
import { getCountryFacts, factsPending } from '../data/facts';
import { isCountryIndexable } from '../seo-stage';
import { countryDisplayName } from '../utils/country-name';
import { escapeHtml } from '../utils';
import { t, getLocale } from '../i18n';
import { countryPageHtml, type Country, type CountryRow } from '@roamzy/shared';
import { mainButton as tgMainButton, haptic as tgHaptic, tgIsAvailable } from '../telegram-mini-app';

const countryArticle: RouteDef = {
  path: '/countries/:slug',
  // The page reads both: the article and the facts block.
  whenReady: () => {
    const pending = [articlesPending(), factsPending()].filter((p) => p !== null);
    return pending.length ? Promise.all(pending) : null;
  },
  routeType: 'country',
  navKey: 'prices',
  metaTitle: 'eSIM by country — Roamzy',
  metaDesc: 'Per-country eSIM pricing and setup info.',
  // 2026-05-04: country article bodies were generated by a templated seed
  // script with ~45% mean cross-article similarity. Google's HCU + Scaled
  // Content Abuse policy targets exactly this. Block indexing while we
  // regenerate (Variant B in docs/archive/seo-rebuild-plan.md). Internal links and
  // user UX unchanged — `follow` preserves link-equity for when we unblock.
  //
  // 2026-05-05: Wave 6 done (192/192 native articles, intra-locale Jaccard
  // 17-23%, cross-locale 5%). Switched from blanket `noindex: true` to a
  // staged predicate so the May 7/14/21 staged un-noindex rolls a cohort
  // at a time. VITE_SEO_INDEX_STAGE controls which slugs flip to indexable
  // (must match backend SEO_INDEX_STAGE so sitemap and meta agree).
  noindex: (ctx) => {
    const slug = ctx.params.slug ?? '';
    // Unknown slug → noindex (also returns 404 status from backend guard)
    if (!getCountryBySlug(slug)) return true;
    // Indexable iff slug is within the current stage cohort.
    return !isCountryIndexable(slug);
  },
  render(ctx) {
    const slug = ctx.params.slug ?? '';
    const country = getCountryBySlug(slug);
    const article = getCountryArticle(slug); // 192/192 EN authored; per-locale catalogs (ru/es/pt/tr) also full

    if (!country) {
      return `
<section class="section">
  <div class="container" style="text-align:center; padding:120px 0;">
    <div class="section__kicker" data-i18n="country.notFound.kicker">${escapeHtml(t('country.notFound.kicker'))}</div>
    <h1 style="font-style:italic; font-size:clamp(2.5rem,6vw,4.5rem);">${escapeHtml(t('country.notFound.title'))}</h1>
    <p style="margin-top:24px;"><a href="/prices" data-i18n="country.notFound.back">${escapeHtml(t('country.notFound.back'))}</a></p>
  </div>
</section>`;
    }

    // One renderer with the server (shared/country-page.ts), fed the same
    // data and catalog strings, so this redraw of the server's page changes
    // nothing (2026-09-30).
    const locale = getLocale();
    const row = (c: Country): CountryRow => ({
      slug: c.slug, name: countryDisplayName(c), flag: c.flag,
      rateUsdtPerMb: c.rateUsdtPerMb, rateUsdtPerGb: c.rateUsdtPerGb, zone: c.zone, isPremium: c.isPremium,
    });
    const body = countryPageHtml({
      locale,
      country: row(country),
      countries: allCountries.map(row),
      articleSlugs: listCountryArticleSlugs(),
      article,
      facts: getCountryFacts(slug),
      t: (key, vars) => t(key as TranslationKey, vars),
      hasBlogTranslation: (post) => blogLocalesFor(post).includes(locale),
      trialCtaHtml: trialCtaHtml({ country: countryDisplayName(country), rate: `$${country.rateUsdtPerMb.toFixed(4)}`, rateUsdtPerMb: country.rateUsdtPerMb }),
      today: new Date().toISOString().slice(0, 10),
    });
    return `<article class="country-route">${body}</article>`;
  },
  /**
   * Telegram Mini App: sticky MainButton at the bottom of the WebView so
   * the primary CTA ("Get {country} eSIM") is always one tap away, even
   * after the user scrolled past the hero. Outside TG this mount is a
   * no-op (the wrapper short-circuits) — the in-hero <button data-action
   * ="get-esim"> remains the only CTA, which is correct for web.
   *
   * Implementation note: rather than re-implementing the openTopUp call
   * here, we programmatically click the existing in-page CTA. That delegates
   * to the same `data-action="get-esim"` handler in main.ts that powers
   * the visible button — auth-gate, sessionStorage pending-slug, openTopUp
   * with country context — all reused. Single source of truth.
   */
  mount(_root, ctx) {
    if (!tgIsAvailable()) return;
    const slug = ctx.params.slug ?? '';
    const country = getCountryBySlug(slug);
    if (!country) return;
    const unmountMainButton = tgMainButton.push({
      text: t('country.buyCta', { country: countryDisplayName(country) }),
      onClick: async () => {
        tgHaptic.tap();
        // Trigger the existing in-page CTA — same auth gate, same flow.
        // querySelector is run inside the click handler (not cached at
        // mount time) because the hero's HTML may have been replaced by
        // a hydration pass on the home → country route transition.
        const cta = document.querySelector<HTMLButtonElement>('.country-hero__cta');
        cta?.click();
        await Promise.resolve();
      },
    });
    return (): void => unmountMainButton();
  },
};

export default countryArticle;
